// One order: what is happening now (status copy), the LUKU token once delivered,
// and the receipt. Polls GET /orders/{id} every ~3s while the order is moving on
// its own, capped at ~3 minutes — then tells the employee they can check back in
// My purchases. An unknown / in-flight state never renders as success or failure.
//
// ⚠️ The token lives in this component's state only: never logged, persisted, or
// placed in a URL.
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft, Ban, CheckCircle2, Copy, Download, Info, Loader2, RefreshCw, RotateCcw, Smartphone,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { DataState, errorVariant } from '@/features/shared/components/ui/DataState';
import { formatMoney } from '@/lib/money';
import { getApiErrorMessage } from '@/lib/apiError';
import { cn } from '@/lib/utils';
import { commerceApi } from '../api/commerceApi';
import { POLL_INTERVAL_MS, POLL_MAX_MS, canBuyAgain, statusInfo, type StatusTone } from '../lib/status';
import { groupToken, operatorName } from '../lib/format';
import type { OperatorOption, Order, OrderToken } from '../types';
import { OrderStatusBadge } from './OrderStatusBadge';

const TONE: Record<StatusTone, string> = {
  pending: 'bg-accent text-accent-foreground',
  info: 'bg-info-soft text-info-strong',
  success: 'bg-success-soft text-success-strong',
  warning: 'bg-warning-soft text-warning-strong',
  neutral: 'bg-cream-dark text-foreground',
};

const TOKEN_POLL_MS = 5000;

const StatusIcon: React.FC<{ statusKey: string; tone: StatusTone }> = ({ statusKey, tone }) => {
  const cls = 'h-6 w-6 shrink-0';
  if (statusKey === 'awaitingPayment') return <Smartphone className={cls} aria-hidden="true" />;
  if (statusKey === 'confirmingPayment') return <Loader2 className={cn(cls, 'animate-spin')} aria-hidden="true" />;
  if (tone === 'pending') return <Loader2 className={cn(cls, 'animate-spin')} aria-hidden="true" />;
  if (tone === 'success') return <CheckCircle2 className={cls} aria-hidden="true" />;
  if (tone === 'warning' || statusKey === 'refunded') return <RotateCcw className={cls} aria-hidden="true" />;
  if (tone === 'neutral') return <Ban className={cls} aria-hidden="true" />;
  return <Info className={cls} aria-hidden="true" />;
};

const Detail: React.FC<{ k: string; v: React.ReactNode; strong?: boolean }> = ({ k, v, strong }) => (
  <div className="flex justify-between gap-4 py-2 text-sm">
    <dt className="text-muted-foreground">{k}</dt>
    <dd className={cn('text-right tabular-nums break-all', strong ? 'font-semibold text-foreground' : 'text-foreground')}>{v}</dd>
  </div>
);

interface Props {
  orderId: string;
  operators: OperatorOption[];
  onBack: () => void;
}

export const OrderStatusView: React.FC<Props> = ({ orderId, operators, onBack }) => {
  const { t, i18n } = useTranslation('everydayServices');
  const [order, setOrder] = useState<Order | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0); // bumps after every fetch so polling continues past a failed poll
  const [pollStart, setPollStart] = useState(() => Date.now());
  const [timedOut, setTimedOut] = useState(false);

  const load = useCallback(async () => {
    try {
      setOrder(await commerceApi.getOrder(orderId));
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setTick((n) => n + 1);
    }
  }, [orderId]);

  useEffect(() => {
    setOrder(null);
    setError(null);
    setTimedOut(false);
    setPollStart(Date.now());
    void load();
  }, [load]);

  const info = statusInfo(order?.status, order?.paymentStatus);
  const inFlight = !!order && info.inFlight;

  useEffect(() => {
    if (!inFlight || timedOut) return undefined;
    if (Date.now() - pollStart >= POLL_MAX_MS) { setTimedOut(true); return undefined; }
    const h = window.setTimeout(() => { void load(); }, POLL_INTERVAL_MS);
    return () => window.clearTimeout(h);
  }, [inFlight, timedOut, pollStart, tick, load]);

  const checkAgain = () => {
    setTimedOut(false);
    setPollStart(Date.now());
    void load();
  };

  // --- LUKU token (electricity, delivered) ---
  const wantsToken = order?.productType === 'ELECTRICITY' && order?.status === 'COMPLETED';
  const [token, setToken] = useState<OrderToken | null>(null);
  const [tokenError, setTokenError] = useState<unknown>(null);
  const [tokenTick, setTokenTick] = useState(0);
  const [tokenStart, setTokenStart] = useState(() => Date.now());

  const loadToken = useCallback(async () => {
    try {
      const tk = await commerceApi.getToken(orderId);
      setToken(tk);
      setTokenError(null);
    } catch (e) {
      setTokenError(e);
    } finally {
      setTokenTick((n) => n + 1);
    }
  }, [orderId]);

  useEffect(() => {
    setToken(null);
    setTokenError(null);
    if (!wantsToken) return;
    setTokenStart(Date.now());
    void loadToken();
  }, [wantsToken, loadToken]);

  // 404 = not issued yet → keep asking for a while (shown as "pending", never blank).
  useEffect(() => {
    if (!wantsToken || token || tokenError || tokenTick === 0) return undefined;
    if (Date.now() - tokenStart >= POLL_MAX_MS) return undefined;
    const h = window.setTimeout(() => { void loadToken(); }, TOKEN_POLL_MS);
    return () => window.clearTimeout(h);
  }, [wantsToken, token, tokenError, tokenTick, tokenStart, loadToken]);

  const copyToken = async () => {
    if (!token?.token) return;
    try {
      await navigator.clipboard.writeText(token.token);
      toast.success(t('token.copied'));
    } catch {
      toast.error(t('errors.copy'));
    }
  };

  const [downloading, setDownloading] = useState(false);
  const downloadReceipt = async () => {
    setDownloading(true);
    try {
      await commerceApi.downloadReceipt(orderId);
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('errors.receipt')));
    } finally {
      setDownloading(false);
    }
  };

  const back = (
    <Button variant="ghost" size="sm" onClick={onBack} className="-ml-2">
      <ArrowLeft className="h-4 w-4" /> {t('order.back')}
    </Button>
  );

  if (!order) {
    return (
      <div className="space-y-4">
        {back}
        <Card>
          <CardContent className="p-0">
            {error && (error as { response?: { status?: number } })?.response?.status === 401
              // Session expired: the purchase is safe on the server and this link
              // (the order id) brings the buyer straight back after signing in.
              ? <DataState variant="denied" title={t('session.title')} message={t('session.message')} />
              : error
                ? <DataState variant={errorVariant(error)} title={t('errors.order')} message={getApiErrorMessage(error, t('errors.order'))} onRetry={checkAgain} />
                : <DataState variant="loading" />}
          </CardContent>
        </Card>
      </div>
    );
  }

  const net = operatorName(order.operator, operators);
  const money = (n: number) => formatMoney(n, order.currency);
  const placed = order.createdAt
    ? new Date(order.createdAt).toLocaleString(i18n.language, { dateStyle: 'medium', timeStyle: 'short' })
    : '';
  const canCheckAgain = timedOut || info.key === 'unknown' || info.key === 'reconciling' || info.key === 'refundPending' || info.key === 'confirmingPayment';
  const buyAgain = canBuyAgain(order.status, order.paymentStatus);
  const recipientText = order.recipientRelation === 'other'
    ? (order.recipientLabel ? `${order.recipientLabel} · ${order.recipient}` : order.recipient)
    : `${t('recipient.myself')} · ${order.recipient}`;

  return (
    <div className="space-y-4">
      {back}

      <Card>
        <CardContent className="space-y-4 p-5">
          <div className={cn('flex items-start gap-3 rounded-xl p-4', TONE[info.tone])} role="status" aria-live="polite">
            <StatusIcon statusKey={info.key} tone={info.tone} />
            <div className="min-w-0">
              <p className="font-semibold">{t(`status.${info.key}.title`, info.title)}</p>
              <p className="mt-0.5 text-sm">{t(`status.${info.key}.message`, info.message)}</p>
            </div>
          </div>

          {timedOut && inFlight && (
            <p className="text-sm text-muted-foreground">{t('order.stillWaiting')}</p>
          )}

          {wantsToken && (
            <div className="rounded-xl border border-border p-4">
              <p className="text-sm font-medium text-muted-foreground">{t('token.title')}</p>
              {token?.token ? (
                <>
                  <p className="mt-2 font-mono text-2xl font-semibold tracking-wider text-foreground break-all select-all">
                    {groupToken(token.token)}
                  </p>
                  {token.units && (
                    <p className="mt-1 text-sm text-muted-foreground">{t('token.units')}: <span className="font-medium text-foreground">{token.units}</span></p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <Button type="button" size="sm" onClick={copyToken}>
                      <Copy className="h-4 w-4" /> {t('token.copy')}
                    </Button>
                  </div>
                  <p className="mt-3 text-xs text-muted-foreground">{t('token.keepPrivate')}</p>
                </>
              ) : tokenError ? (
                <DataState compact variant={errorVariant(tokenError)} message={getApiErrorMessage(tokenError, t('errors.token'))} onRetry={() => { setTokenError(null); setTokenStart(Date.now()); void loadToken(); }} />
              ) : (
                <p className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> {t('token.pending')}
                </p>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            {info.receipt && (
              <Button type="button" variant="outline" size="sm" onClick={downloadReceipt} disabled={downloading}>
                {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                {t('order.receipt')}
              </Button>
            )}
            {canCheckAgain && (
              <Button type="button" variant="outline" size="sm" onClick={checkAgain}>
                <RefreshCw className="h-4 w-4" /> {t('order.checkAgain')}
              </Button>
            )}
            {/* Never offered while the outcome is uncertain — they might pay twice. */}
            {buyAgain && (
              <Button type="button" variant="ghost" size="sm" onClick={onBack}>{t('order.buyAgain')}</Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{t('order.details')}</CardTitle>
          <OrderStatusBadge status={order.status} paymentStatus={order.paymentStatus} />
        </CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Detail k={t('order.product')} v={order.productName || t(`products.${order.productType}`, order.productType)} />
            {order.validity && <Detail k={t('order.validity')} v={order.validity} />}
            {net && <Detail k={t('order.network')} v={net} />}
            <Detail k={t('order.recipient')} v={recipientText} />
            {order.payer && <Detail k={t('order.payer')} v={order.payer} />}
            <Detail k={t('order.amount')} v={money(order.faceValue)} />
            {order.fee > 0 && <Detail k={t('order.fee')} v={money(order.fee)} />}
            <Detail k={t('order.total')} v={money(order.customerTotal || order.faceValue)} strong />
            {placed && <Detail k={t('order.placed')} v={placed} />}
          </dl>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderStatusView;
